import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  CheckCircle,
  Clock,
  MapPin,
  Truck,
  AlertCircle,
  Camera,
  Mic,
  Send,
  DollarSign,
  FileText,
  Package,
  PlayCircle,
  Pause,
  X,
  Upload,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { ticketSystem, ServiceTicket, TicketStatus } from '../utils/tickets/ticketSystem';

interface VendorPortalProps {
  publicLink: string;
}

export default function VendorPortal({ publicLink }: VendorPortalProps) {
  const [ticket, setTicket] = useState<ServiceTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [actualCost, setActualCost] = useState('');
  const [laborHours, setLaborHours] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadTicket();
  }, [publicLink]);

  useEffect(() => {
    let interval: any;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const loadTicket = () => {
    setLoading(true);
    const ticketData = ticketSystem.getTicketByPublicLink(publicLink);
    
    if (!ticketData) {
      setTicket(null);
      setLoading(false);
      return;
    }

    setTicket(ticketData);
    setLoading(false);
  };

  const handleStatusChange = (newStatus: TicketStatus) => {
    if (!ticket) return;

    ticketSystem.updateTicket(ticket.id, { status: newStatus }, {
      type: 'vendor',
      id: ticket.assignedTo?.id || 'vendor',
      name: ticket.assignedTo?.name || 'Vendor',
    });

    loadTicket();
    toast.success('Status updated successfully');
  };

  const handleAddComment = () => {
    if (!ticket || !newComment.trim()) return;

    ticketSystem.addTicketComment(ticket.id, newComment, {
      type: 'vendor',
      id: ticket.assignedTo?.id || 'vendor',
      name: ticket.assignedTo?.name || 'Vendor',
    });

    setNewComment('');
    loadTicket();
    toast.success('Comment added');
  };

  const handleUpdateCost = () => {
    if (!ticket) return;

    const cost = parseFloat(actualCost);
    const hours = parseFloat(laborHours);

    if (isNaN(cost) || cost < 0) {
      toast.error('Please enter a valid cost');
      return;
    }

    ticketSystem.updateTicket(ticket.id, {
      actualCost: cost,
      laborHours: hours || undefined,
    }, {
      type: 'vendor',
      id: ticket.assignedTo?.id || 'vendor',
      name: ticket.assignedTo?.name || 'Vendor',
    });

    loadTicket();
    toast.success('Cost updated successfully');
  };

  // Voice Memo Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        
        // Save voice memo
        if (ticket) {
          ticketSystem.addVoiceMemo(ticket.id, {
            url,
            duration: recordingTime,
            createdBy: ticket.assignedTo?.id || 'vendor',
            createdByName: ticket.assignedTo?.name || 'Vendor',
          });
          loadTicket();
          toast.success('Voice memo saved');
        }

        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setMediaRecorder(null);
      setIsRecording(false);
    }
  };

  // Photo Upload
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedPhotos(prev => [...prev, ...files].slice(0, 10)); // Max 10 photos
  };

  const handlePhotoUpload = async () => {
    if (!ticket || selectedPhotos.length === 0) return;

    setUploading(true);

    try {
      // In production, upload to cloud storage (S3, Cloudinary, etc.)
      // For now, create object URLs
      for (const file of selectedPhotos) {
        const url = URL.createObjectURL(file);
        
        ticketSystem.addPhoto(ticket.id, {
          url,
          thumbnail: url,
          caption: file.name,
          createdBy: ticket.assignedTo?.id || 'vendor',
          createdByName: ticket.assignedTo?.name || 'Vendor',
        });
      }

      setSelectedPhotos([]);
      loadTicket();
      toast.success(`${selectedPhotos.length} photo(s) uploaded`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const removeSelectedPhoto = (index: number) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-gray-600">
              This service ticket link is no longer active or has been closed.
              Please contact the operator for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending_parts': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'emergency': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Truck className="h-6 w-6" />
            <div>
              <h1 className="text-xl font-bold">Service Ticket</h1>
              <p className="text-xs text-blue-100">{ticket.ticketNumber}</p>
            </div>
          </div>
          <Badge className={getStatusColor(ticket.status)}>
            {ticket.status.replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Priority Banner */}
        {ticket.priority === 'emergency' && (
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 flex items-center space-x-3 animate-pulse">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-red-900">EMERGENCY SERVICE</p>
              <p className="text-sm text-red-700">Immediate attention required</p>
            </div>
          </div>
        )}

        {/* Ticket Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <span>Service Details</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 text-lg mb-1">{ticket.title}</h3>
              <p className="text-gray-600">{ticket.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Service Type</label>
                <p className="text-sm font-medium text-gray-900 mt-0.5">
                  {ticket.serviceType.replace(/_/g, ' ').toUpperCase()}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Priority</label>
                <div className="mt-0.5">
                  <Badge className={`${getPriorityColor(ticket.priority)} border`}>
                    {ticket.priority.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-start space-x-2 text-gray-700 mb-2">
                <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{ticket.location}</span>
              </div>
              {ticket.scheduledDate && (
                <div className="flex items-start space-x-2 text-gray-700">
                  <Clock className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">
                    Scheduled: {new Date(ticket.scheduledDate).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {ticket.estimatedCost && (
              <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-blue-900 font-medium">Estimated Cost</span>
                <span className="text-lg font-bold text-blue-900">${ticket.estimatedCost.toFixed(2)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Status Update */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Update Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => handleStatusChange('in_progress')}
                variant={ticket.status === 'in_progress' ? 'default' : 'outline'}
                className={`h-20 flex flex-col items-center justify-center ${
                  ticket.status === 'in_progress' ? 'bg-yellow-600 hover:bg-yellow-700' : ''
                }`}
              >
                <Clock className="h-5 w-5 mb-1" />
                <span className="text-xs">In Progress</span>
              </Button>

              <Button
                onClick={() => handleStatusChange('pending_parts')}
                variant={ticket.status === 'pending_parts' ? 'default' : 'outline'}
                className={`h-20 flex flex-col items-center justify-center ${
                  ticket.status === 'pending_parts' ? 'bg-orange-600 hover:bg-orange-700' : ''
                }`}
              >
                <Package className="h-5 w-5 mb-1" />
                <span className="text-xs">Need Parts</span>
              </Button>

              <Button
                onClick={() => handleStatusChange('on_hold')}
                variant={ticket.status === 'on_hold' ? 'default' : 'outline'}
                className={`h-20 flex flex-col items-center justify-center ${
                  ticket.status === 'on_hold' ? 'bg-red-600 hover:bg-red-700' : ''
                }`}
              >
                <AlertCircle className="h-5 w-5 mb-1" />
                <span className="text-xs">On Hold</span>
              </Button>

              <Button
                onClick={() => handleStatusChange('completed')}
                variant={ticket.status === 'completed' ? 'default' : 'outline'}
                className={`h-20 flex flex-col items-center justify-center ${
                  ticket.status === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''
                }`}
              >
                <CheckCircle className="h-5 w-5 mb-1" />
                <span className="text-xs">Completed</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Photo Upload */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <Camera className="h-5 w-5 text-blue-600" />
              <span>Add Photos</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              type="file"
              id="photo-upload"
              accept="image/*"
              multiple
              onChange={handlePhotoSelect}
              className="hidden"
            />
            
            {selectedPhotos.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  {selectedPhotos.map((photo, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Selected ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removeSelectedPhoto(index)}
                        aria-label="Remove Photo"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handlePhotoUpload}
                  disabled={uploading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload {selectedPhotos.length} Photo{selectedPhotos.length > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            )}

            <label
              htmlFor="photo-upload"
              className="flex items-center justify-center space-x-2 border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Camera className="h-6 w-6 text-gray-400" />
              <span className="text-gray-600 font-medium">Take or Select Photos</span>
            </label>

            {ticket.photos.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Uploaded Photos ({ticket.photos.length})
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {ticket.photos.map((photo) => (
                    <div key={photo.id} className="aspect-square">
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Photo'}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Voice Memos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <Mic className="h-5 w-5 text-blue-600" />
              <span>Voice Memos</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!isRecording ? (
              <Button
                onClick={startRecording}
                className="w-full h-16 bg-red-600 hover:bg-red-700 text-white"
              >
                <Mic className="h-5 w-5 mr-2" />
                Start Recording
              </Button>
            ) : (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="font-semibold text-red-900">Recording...</span>
                  </div>
                  <span className="font-mono text-lg text-red-900">{formatTime(recordingTime)}</span>
                </div>
                <Button
                  onClick={stopRecording}
                  variant="outline"
                  className="w-full border-red-500 text-red-700 hover:bg-red-100"
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Stop Recording
                </Button>
              </div>
            )}

            {ticket.voiceMemos.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  Recordings ({ticket.voiceMemos.length})
                </p>
                {ticket.voiceMemos.map((memo) => (
                  <div key={memo.id} className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <PlayCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {formatTime(memo.duration)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(memo.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <audio src={memo.url} controls className="h-8" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost & Labor */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <span>Cost & Labor</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actual Cost ($)
              </label>
              <Input
                type="number"
                value={actualCost}
                onChange={(e) => setActualCost(e.target.value)}
                placeholder="0.00"
                className="h-12 text-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Labor Hours
              </label>
              <Input
                type="number"
                value={laborHours}
                onChange={(e) => setLaborHours(e.target.value)}
                placeholder="0"
                className="h-12 text-lg"
              />
            </div>

            <Button
              onClick={handleUpdateCost}
              className="w-full h-12 bg-green-600 hover:bg-green-700"
            >
              <Check className="h-5 w-5 mr-2" />
              Update Cost
            </Button>

            {ticket.actualCost && (
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-sm text-green-700">Current Cost</p>
                <p className="text-2xl font-bold text-green-900">
                  ${ticket.actualCost.toFixed(2)}
                </p>
                {ticket.laborHours && (
                  <p className="text-sm text-green-700 mt-1">
                    {ticket.laborHours} hours
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Notes & Comments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add notes about the service..."
              rows={4}
              className="resize-none"
              aria-label="New Comment"
            />
            <Button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Add Comment
            </Button>

            {ticket.updates.length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-sm font-medium text-gray-700">Activity Timeline</p>
                <div className="space-y-3">
                  {ticket.updates.slice().reverse().map((update) => (
                    <div key={update.id} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-900">{update.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-600">{update.createdBy.name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(update.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
